import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, CloudOff, RefreshCw, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { getPendingCount, syncPendingCargas } from '../../lib/dieselOfflineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function OfflineBanner() {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [pendingCount, setPendingCount] = useState(0);
    const [syncing, setSyncing] = useState(false);
    const [justSynced, setJustSynced] = useState(false);

    useEffect(() => {
        const goOnline = () => {
            setIsOnline(true);
            autoSync();
        };
        const goOffline = () => setIsOnline(false);

        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);

        checkPending();
        const interval = setInterval(checkPending, 10000);

        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
            clearInterval(interval);
        };
    }, []);

    const checkPending = async () => {
        try {
            const count = await getPendingCount();
            setPendingCount(count);
        } catch { /* ignore */ }
    };

    const autoSync = async () => {
        const count = await getPendingCount();
        if (count > 0) handleSync();
    };

    const handleSync = async () => {
        if (syncing || !isOnline) return;
        setSyncing(true);
        try {
            const token = localStorage.getItem('token');
            const result = await syncPendingCargas(API_URL, token);
            if (result.synced > 0) {
                toast.success(`${result.synced} carga${result.synced > 1 ? 's' : ''} sincronizada${result.synced > 1 ? 's' : ''}`);
                setJustSynced(true);
                setTimeout(() => setJustSynced(false), 3000);
            }
            if (result.failed > 0) {
                toast.error(`${result.failed} carga${result.failed > 1 ? 's' : ''} no se pudo sincronizar`);
            }
            await checkPending();
        } catch {
            toast.error('Error al sincronizar');
        } finally {
            setSyncing(false);
        }
    };

    if (isOnline && pendingCount === 0 && !justSynced) return null;

    return (
        <div
            className={`flex items-center justify-between gap-3 px-4 py-2 rounded-lg text-sm transition-all ${
                !isOnline
                    ? 'bg-red-500/10 border border-red-500/30 text-red-600'
                    : justSynced
                        ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-600'
                        : pendingCount > 0
                            ? 'bg-amber-500/10 border border-amber-500/30 text-amber-600'
                            : ''
            }`}
            data-testid="offline-banner"
        >
            <div className="flex items-center gap-2">
                {!isOnline ? (
                    <>
                        <WifiOff className="h-4 w-4" />
                        <span className="font-medium">Sin conexion</span>
                        <span className="text-xs opacity-75">Las cargas se guardan localmente</span>
                    </>
                ) : justSynced ? (
                    <>
                        <Check className="h-4 w-4" />
                        <span className="font-medium">Sincronizado correctamente</span>
                    </>
                ) : (
                    <>
                        <CloudOff className="h-4 w-4" />
                        <span className="font-medium">{pendingCount} carga{pendingCount > 1 ? 's' : ''} pendiente{pendingCount > 1 ? 's' : ''}</span>
                    </>
                )}
            </div>
            {isOnline && pendingCount > 0 && (
                <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSync}
                    disabled={syncing}
                    className="h-7 text-xs"
                    data-testid="sync-btn"
                >
                    <RefreshCw className={`h-3 w-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Sincronizando...' : 'Sincronizar'}
                </Button>
            )}
        </div>
    );
}

export function PendingBadge() {
    const [count, setCount] = useState(0);

    useEffect(() => {
        const check = async () => {
            try {
                const c = await getPendingCount();
                setCount(c);
            } catch { /* ignore */ }
        };
        check();
        const interval = setInterval(check, 5000);
        return () => clearInterval(interval);
    }, []);

    if (count === 0) return null;

    return (
        <Badge
            variant="destructive"
            className="ml-1 h-5 min-w-[20px] px-1 text-xs flex items-center justify-center rounded-full"
            data-testid="pending-badge"
        >
            {count}
        </Badge>
    );
}
